/**
 * Utskriften med talare och tider, och (för ägaren) namnbyte på talarna.
 */
import { useId, useMemo, useState } from 'react';
import { Button, Input } from '@/components/ui';
import type { Segment } from '../../../../netlify/functions/_shared/moten/typer';
import { Besked } from './MotesDelar';
import { klockText } from './motesText';

interface Props {
  segment: Segment[];
  talarnamn: Record<string, string>;
  kanAndra: boolean;
  onSparaNamn: (namn: Record<string, string>) => Promise<void>;
}

export function Utskrift({ segment, talarnamn, kanAndra, onSparaNamn }: Props) {
  const uid = useId();
  const talare = useMemo(() => {
    const sedda: string[] = [];
    for (const s of segment) if (!sedda.includes(s.talare)) sedda.push(s.talare);
    return sedda;
  }, [segment]);

  const [dopar, setDopar] = useState(false);
  const [utkast, setUtkast] = useState<Record<string, string>>({});
  const [sparar, setSparar] = useState(false);
  const [fel, setFel] = useState<string | null>(null);

  const namn = (t: string) => talarnamn[t]?.trim() || t;

  function oppna() {
    setUtkast(Object.fromEntries(talare.map((t) => [t, talarnamn[t] ?? ''])));
    setFel(null);
    setDopar(true);
  }

  async function spara() {
    setSparar(true);
    setFel(null);
    try {
      await onSparaNamn(Object.fromEntries(talare.map((t) => [t, (utkast[t] ?? '').trim()])));
      setDopar(false);
    } catch (e) {
      setFel((e as Error).message || 'Namnen kunde inte sparas.');
    } finally {
      setSparar(false);
    }
  }

  return (
    <div>
      {kanAndra && talare.length > 0 && !dopar && (
        <div className="mb-4">
          <Button variant="secondary" size="sm" onClick={oppna}>
            Döp om talare
          </Button>
        </div>
      )}

      {dopar && (
        <div className="mb-5 rounded-lg border border-ink-200 bg-surface-sunken p-4">
          <p className="text-[12.5px] text-ink-600 mb-3">
            Skriv vem som är vem. Ett tomt fält visar talarens ursprungliga beteckning.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {talare.map((t, i) => (
              <div key={t} className="min-w-0">
                <label htmlFor={`${uid}-${i}`} className="block text-[12px] font-semibold text-ink-700 mb-1">
                  {t}
                </label>
                <Input
                  id={`${uid}-${i}`}
                  value={utkast[t] ?? ''}
                  maxLength={80}
                  onChange={(e) => setUtkast((u) => ({ ...u, [t]: e.target.value }))}
                  placeholder="Namn"
                  autoComplete="off"
                />
              </div>
            ))}
          </div>
          {fel && (
            <div className="mt-3">
              <Besked ton="fel">{fel}</Besked>
            </div>
          )}
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDopar(false)} disabled={sparar}>
              Avbryt
            </Button>
            <Button size="sm" onClick={() => void spara()} loading={sparar}>
              Spara namn
            </Button>
          </div>
        </div>
      )}

      <ol className="space-y-3.5">
        {segment.map((s, i) => {
          // Samma talare i följd: namnet visas bara första gången.
          const nyTalare = i === 0 || segment[i - 1].talare !== s.talare;
          return (
            <li key={i} className="grid grid-cols-[60px_1fr] gap-3">
              <span className="text-[12px] tabular-nums text-ink-500 pt-0.5">{klockText(s.start_sek)}</span>
              <div className="min-w-0">
                {nyTalare && <p className="text-[12.5px] font-semibold text-ink-900">{namn(s.talare)}</p>}
                <p className="text-[13.5px] text-ink-800 leading-relaxed break-words">{s.text}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
